/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { SvgHandler } from "../../svg-handler/svg-handler";
import close from "../../../../assets/icons/social/close.svg";
import LoaderOverlay from "../loader-overlay";
import { MEMBER_METADATA_UPDATED, SHOW_MEMBER_METADATA_POPUP, MEMBER_METADATA } from "../../../constants";

import "./capture-user-data.m.css";

export const CaptureUserData = ({
  member = {},
  getSubscriberMetadata,
  setSubscriberMetadata,
  isInline = false,
  subscriptionConfig = {},
  onSubmit,
  buttonText,
  buttonStyle,
  isPB = false,
  mode = "light",
}) => {
  const isMemberMetadataUpdated = useSelector((state) =>
    get(state, ["memberMetadata", "isMemberMetadataUpdated"], false)
  );
  const showMemberMetadataPopup = useSelector((state) =>
    get(state, ["memberMetadata", "showMemberMetadataPopup"], true)
  );
  const { enableLocalization = false, localizedElements = {} } = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "general", "localization"], {})
  );
  const skipPhoneValidation = useSelector((state) =>
    get(state, ["qt", "config", "pagebuilder-config", "general", "skipPhoneValidation"], false)
  );
  const { shippingAddress: localizedShippingAddress = {}, fieldErrors: localizedFieldErrors = {} } = enableLocalization
    ? localizedElements
    : {};
  const {
    heading: localizedHeading = "",
    name: localizedName = "",
    address: localizedAddress = "",
    city: localizedCity = "",
    state: localizedState = "",
    country: localizedCountry = "",
    postCode: localizedPostCode = "",
    dailCode: localizedDailCode = "",
    phoneNumber: localizedPhoneNumber = "",
    email: localizedEmail = "",
    button: localizedButton = "",
  } = localizedShippingAddress;
  const {
    invalidField: localizedInvalidField,
    invalidPhoneNumber: localizedInvalidPhoneNumber,
    invalidMail: localizedInvalidMail,
    invalidDialCode: localizedInvalidDialCode,
  } = localizedFieldErrors;
  const isPbPreview = Boolean(isPB);
  const reduxMember = useSelector((state) => get(state, ["member"], null));
  const currentMember = !isPbPreview ? (member && (member.id || member.email) ? member : reduxMember) : null;

  const [formData, setFormData] = useState({
    name: get(currentMember, ["name"], ""),
    address: "",
    email: get(currentMember, ["email"], ""),
    city: "",
    country: "",
    state: "",
    postCode: "",
    dialCode: "",
    phoneNumber: "",
  });

  useEffect(() => {
    if (!isPbPreview && currentMember && (currentMember.name || currentMember.email)) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || get(currentMember, ["name"], ""),
        email: prev.email || get(currentMember, ["email"], ""),
      }));
    }
  }, [currentMember, isPbPreview]);

  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  const isMandatory = (fieldKey) => {
    if (!isInline) return true; // Legacy modal requires all fields by default
    const configMap = {
      email: "mandatoryEmail",
      name: "mandatoryFullName",
      address: "mandatoryStreetAddress",
      city: "mandatoryCity",
      state: "mandatoryState",
      country: "mandatoryCountry",
      postCode: "mandatoryPin",
      phoneNumber: "mandatoryPhone",
      dialCode: "mandatoryPhone",
    };
    return Boolean(subscriptionConfig[configMap[fieldKey]]);
  };

  const fields = [
    { name: "name", label: localizedName || "Full Name", placeholder: "", type: "text" },
    { name: "address", label: localizedAddress || "Street Address", placeholder: "", type: "text" },
    { name: "city", label: localizedCity || "City", placeholder: "", type: "text" },
    { name: "state", label: localizedState || "State", placeholder: "", type: "text" },
    { name: "country", label: localizedCountry || "Country", placeholder: "", type: "text" },
    { name: "postCode", label: localizedPostCode || "Pin", placeholder: "", type: "text" },
    { name: "dialCode", label: localizedDailCode || "Country Code", placeholder: "", type: "tel" },
    { name: "phoneNumber", label: localizedPhoneNumber || "Phone Number", placeholder: "", type: "tel" },
    { name: "email", label: localizedEmail || "Email Address", placeholder: "", type: "email" },
  ];

  const handleValidation = () => {
    const errors = {};
    let formIsValid = true;
    const regexForEmail = RegExp(/^[_a-z0-9-]+(\.[_a-z0-9-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,4})$/);
    const regexForPhone = RegExp(/^[0-9]{10}$/);
    const regexForPhoneBasic = RegExp(/^[0-9]+$/);
    const regexForDialCode = RegExp(/^[+]?[0-9]*$/);

    const checkFields = ["name", "city", "state", "address", "postCode", "phoneNumber", "country", "dialCode", "email"];

    checkFields.forEach((item) => {
      const val = formData[item];
      if (isMandatory(item) && (!val || !String(val).trim())) {
        formIsValid = false;
        errors[item] = localizedInvalidField || "Cannot be empty";
      }
    });

    if (formData.email && !regexForEmail.test(formData.email.toLowerCase())) {
      formIsValid = false;
      errors.email = localizedInvalidMail || "Invalid email-ID";
    }

    if (formData.phoneNumber) {
      const cleanPhone = String(formData.phoneNumber).replace(/\s+/g, "");
      if (!skipPhoneValidation && !regexForPhone.test(cleanPhone)) {
        formIsValid = false;
        errors.phoneNumber = localizedInvalidPhoneNumber || "Invalid phone number";
      } else if (skipPhoneValidation && !regexForPhoneBasic.test(cleanPhone)) {
        formIsValid = false;
        errors.phoneNumber = localizedInvalidPhoneNumber || "Phone number must contain only digits";
      }
    }

    if (formData.dialCode && !regexForDialCode.test(formData.dialCode)) {
      formIsValid = false;
      errors.dialCode = localizedInvalidDialCode || "Invalid Dial code";
    }

    setErrors(errors);
    return formIsValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPB) return;

    if (!handleValidation()) {
      return null;
    }

    if (onSubmit) {
      onSubmit(formData);
      return;
    }

    const { name, address, city, state, country, postCode, phoneNumber, email, dialCode } = formData;

    const subscriberDetails = {
      address: {
        line1: name,
        line2: address,
        city: city,
        state: state,
        country: country,
        pincode: postCode,
      },
      phone_number: phoneNumber,
      other: {
        email: email,
        dialCode: dialCode,
      },
    };

    try {
      if (subscriberDetails) {
        dispatch({
          type: MEMBER_METADATA_UPDATED,
          isMemberMetadataUpdated: !isMemberMetadataUpdated,
        });
        dispatch({
          type: MEMBER_METADATA,
          data: subscriberDetails,
        });
        if (setSubscriberMetadata) {
          await setSubscriberMetadata(subscriberDetails);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleForm = (e) => {
    dispatch({
      type: SHOW_MEMBER_METADATA_POPUP,
      showMemberMetadataPopup: !showMemberMetadataPopup,
    });
  };

  const handleChange = (e, name) => {
    setFormData({ ...formData, [name]: e.target.value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  if (!isInline && (!showMemberMetadataPopup || isMemberMetadataUpdated)) {
    return null;
  }

  if (isEmpty(formData) && !isInline) {
    return <LoaderOverlay />;
  }

  const renderFormFields = () => {
    const fieldsToRender = isInline ? fields.filter((f) => f.name !== "dialCode") : fields;

    const fieldTestIdMap = {
      name: "subscription-full-name-input",
      address: "subscription-street-address-input",
      city: "subscription-city-input",
      state: "subscription-state-input",
      country: "subscription-country-input",
      postCode: "subscription-pin-input",
      dialCode: "subscription-phone-code-input",
      phoneNumber: "subscription-phone-number-input",
      email: "subscription-email-input",
    };

    return (
      <div styleName="input-grid">
        {fieldsToRender.map(({ name, label, placeholder, type }) => {
          if (isInline && name === "phoneNumber") {
            return (
              <div styleName="form-row-code-phone custom-field" key={name}>
                <div styleName="code-col">
                  <label styleName="label">
                    {localizedDailCode || "Code"}
                    {isMandatory("dialCode") && <span styleName="required">*</span>}
                  </label>
                  <input
                    styleName={`input-field ${errors.dialCode ? "error-msg" : ""}`}
                    type="tel"
                    value={formData.dialCode}
                    onChange={(e) => handleChange(e, "dialCode")}
                    placeholder=""
                    data-test-id="subscription-phone-code-input"
                  />
                  {errors.dialCode && <div styleName="show-error-message">{errors.dialCode}</div>}
                </div>

                <div styleName="phone-col">
                  <label styleName="label">
                    {label}
                    {isMandatory("phoneNumber") && <span styleName="required">*</span>}
                  </label>
                  <input
                    styleName={`input-field ${errors.phoneNumber ? "error-msg" : ""}`}
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleChange(e, "phoneNumber")}
                    placeholder={placeholder}
                    data-test-id="subscription-phone-number-input"
                  />
                  {errors.phoneNumber && <div styleName="show-error-message">{errors.phoneNumber}</div>}
                </div>
              </div>
            );
          }

          return (
            <div
              styleName={`field-wrapper ${
                name === "name" || name === "email" || name === "address" ? "custom-field" : ""
              }`}
              key={name}
            >
              {label && (
                <label styleName="label">
                  {label}
                  {isMandatory(name) && <span styleName="required">*</span>}
                </label>
              )}
              <input
                styleName={`input-field ${errors[name] ? "error-msg" : ""}`}
                type={type}
                value={formData[name]}
                onChange={(e) => handleChange(e, name)}
                placeholder={placeholder}
                name={name}
                data-test-id={fieldTestIdMap[name] || `subscription-${name}-input`}
              />
              {errors[name] && <div styleName="show-error-message">{errors[name]}</div>}
            </div>
          );
        })}
      </div>
    );
  };

  if (isInline) {
    return (
      <div styleName={`inline-form-wrapper ${mode === "dark" ? "dark" : ""}`}>
        <form onSubmit={handleSubmit}>
          {renderFormFields()}
          <button
            type="submit"
            styleName={`submit-btn ${isInline ? "loginless-btn" : ""}`}
            style={buttonStyle}
            data-test-id="subscription-proceed-payment-btn"
          >
            {buttonText || localizedButton || "Continue"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div styleName="content-wrapper">
      <div styleName="overlay">
        <div styleName="wrapper">
          <div styleName="close-btn" onClick={(e) => toggleForm(e)}>
            <SvgHandler width="24px" height="24px" xlinkHref={close} />
          </div>
          <h5 styleName="shipping-address" data-test-id="subscription-shipping-heading">{localizedHeading || "Enter Shipping Address"}</h5>
          <form onSubmit={handleSubmit}>
            {renderFormFields()}
            <button type="submit" styleName="submit-btn" data-test-id="subscription-proceed-payment-btn">
              {localizedButton || "Continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

CaptureUserData.propTypes = {
  member: PropTypes.object,
  getSubscriberMetadata: PropTypes.func,
  setSubscriberMetadata: PropTypes.func,
  isInline: PropTypes.bool,
  subscriptionConfig: PropTypes.object,
  onSubmit: PropTypes.func,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  buttonStyle: PropTypes.object,
  isPB: PropTypes.bool,
  mode: PropTypes.string,
};
